import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import {
  PyThreadPanelContainer,
  PyThreadPanelHeader,
  PyThreadPanelHeaderContainer,
  PyThreadPanelSegmentedButton,
  PyThreadPanelSegmentedButtonContainer,
} from './styled';

import { useMergedThreads } from 'providers/MergedThreadsProvider';
import { Thread } from './Thread';

import { useTranslation } from 'react-i18next';
import { useMultiEditorComments } from '../../hooks/useMultiEditorComments';
import { ThreadInput } from './ThreadInput';
import { groupThreadsByPosition, filterGroupedThreads, groupThreadsByBlockId } from './threadHelpers';

import '@liveblocks/react-ui/styles.css';
import { useThreadPosition } from '../../contexts/ThreadPositionContext';

export const ThreadPanel = ({ contentId }: { contentId: string }) => {
  const { t } = useTranslation();
  const { threads } = useMergedThreads();
  const [threadTab, setThreadTab] = useState<'active' | 'resolved'>('active');
  const [isMountedThreads, setIsMountedThreads] = useState(false);
  const threadPanelRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const { clearPositions } = useThreadPosition();

  const [openedGroups, setOpenedGroups] = useState(-1);

  const {
    activeThreadId,
    autoFocusThreadId,
    resolveThread,
    markActiveThread,
    addComment,
    focusThread,
    clearAutoFocusThreadId,
    blurThreads,
    editComment,
    deleteComment,
    deleteThread,
    setActiveThreadId,
    setVisibleThreadsOnAllEditors,
    setupThreadClickHandlerOnAllEditors,
    cleanupThreadClickHandlerOnAllEditors,
    cleanupVisibleThreadsOnAllEditors,
    getAllEditors,
    getThreadsWithoutEditor,
  } = useMultiEditorComments(contentId);

  const filteredThreads = useMemo(() => {
    const threadsWithoutEditor = getThreadsWithoutEditor();

    return {
      active: threads.filter((thread) => !thread.resolved && !threadsWithoutEditor.includes(thread.id)),
      resolved: threads.filter((thread) => thread.resolved && !threadsWithoutEditor.includes(thread.id)),
      withoutEditor: threads.filter((thread) => threadsWithoutEditor.includes(thread.id)),
    };
  }, [threads, getThreadsWithoutEditor]);

  const groupedThreadPositions = useMemo(() => {
    const allEditors = getAllEditors();
    const allGroupedPositions: Array<{
      position: number;
      threads: Array<{
        threadId: string;
        isResolved: boolean;
      }>;
    }> = [];

    allEditors.forEach((editor) => {
      if (editor.commands?.getGroupedThreadPositions) {
        editor.commands.getGroupedThreadPositions();
        const positions = editor.storage.commentThread?.groupedThreadPositions || [];
        allGroupedPositions.push(...positions);
      }
    });

    return allGroupedPositions;
  }, [getAllEditors, threads]);

  const groupedThreads = useMemo(() => {
    return groupThreadsByPosition(groupedThreadPositions, threads);
  }, [groupedThreadPositions, threads]);

  const filteredGroupedThreads = useMemo(() => {
    return filterGroupedThreads(groupedThreads, threadTab);
  }, [groupedThreads, threadTab]);

  const threadsWithoutEditor = useMemo(() => {
    return groupThreadsByBlockId(filteredThreads.withoutEditor);
  }, [filteredThreads.withoutEditor]);

  const handleThreadClick = useCallback(
    (threadId: string) => {
      const threadGroup = groupedThreads.find((group) => group.threads.some((thread) => thread.thread.id === threadId));
      const groupIndex = groupedThreads.indexOf(threadGroup || { position: 0, threads: [] });
      if (threadGroup && threadGroup.threads.length > 1) {
        if (groupIndex !== openedGroups) {
          setActiveThreadId(null);
          setOpenedGroups(groupIndex);
          return;
        }
      } else {
        setOpenedGroups(-1);
      }

      focusThread(threadId);
      setActiveThreadId(threadId);
    },
    [focusThread, setActiveThreadId, groupedThreads, openedGroups]
  );

  useEffect(() => {
    setupThreadClickHandlerOnAllEditors(handleThreadClick);
    setIsMountedThreads(true);

    return () => {
      cleanupThreadClickHandlerOnAllEditors();
      setIsMountedThreads(false);
    };
  }, [setupThreadClickHandlerOnAllEditors, handleThreadClick]);

  useEffect(() => {
    if (threads && filteredThreads[threadTab].length > 0) {
      setVisibleThreadsOnAllEditors(filteredThreads[threadTab]);
    }

    return () => {
      cleanupVisibleThreadsOnAllEditors();
    };
  }, [filteredThreads, setVisibleThreadsOnAllEditors, threadTab]);

  useEffect(() => {
    if (!activeThreadId) {
      blurThreads();
    }
  }, [activeThreadId, blurThreads]);

  useEffect(() => {
    if (!activeThreadId) {
      return;
    }

    const activeThread = threads.find((thread) => thread.id === activeThreadId);
    if (activeThread) {
      setThreadTab(activeThread.resolved ? 'resolved' : 'active');
    }
  }, [activeThreadId, threads]);

  useEffect(() => {
    if (!activeThreadId || !autoFocusThreadId) {
      return;
    }

    if (autoFocusThreadId !== activeThreadId) {
      return;
    }

    void focusThread(autoFocusThreadId);
    clearAutoFocusThreadId();
  }, [activeThreadId, autoFocusThreadId, clearAutoFocusThreadId, focusThread]);

  useLayoutEffect(() => {
    clearPositions();
  }, [openedGroups, filteredGroupedThreads, clearPositions]);

  useEffect(() => {
    const syncHeaderWidth = () => {
      if (threadPanelRef.current && headerRef.current) {
        const parentWidth = threadPanelRef.current.offsetWidth;
        headerRef.current.style.width = `${parentWidth}px`;
      }
    };

    syncHeaderWidth();

    const resizeObserver = new ResizeObserver(syncHeaderWidth);
    if (threadPanelRef.current) {
      resizeObserver.observe(threadPanelRef.current);
    }

    window.addEventListener('resize', syncHeaderWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncHeaderWidth);
    };
  }, []);

  return (
    <PyThreadPanelContainer ref={threadPanelRef}>
      <PyThreadPanelHeaderContainer ref={headerRef} sx={{ borderRadius: 4 }}>
        <PyThreadPanelHeader>{t('comments.header')}</PyThreadPanelHeader>

        <PyThreadPanelSegmentedButtonContainer>
          <PyThreadPanelSegmentedButton
            sx={{
              borderTopLeftRadius: 100,
              borderBottomLeftRadius: 100,
            }}
            isActive={threadTab === 'active'}
            onClick={() => setThreadTab('active')}
          >
            {t('comments.active')} ({filteredThreads.active.length})
          </PyThreadPanelSegmentedButton>
          <PyThreadPanelSegmentedButton
            sx={{
              borderTopRightRadius: 100,
              borderBottomRightRadius: 100,
            }}
            isActive={threadTab === 'resolved'}
            onClick={() => setThreadTab('resolved')}
          >
            {t('comments.resolved')} ({filteredThreads.resolved.length + filteredThreads.withoutEditor.length})
          </PyThreadPanelSegmentedButton>
        </PyThreadPanelSegmentedButtonContainer>
      </PyThreadPanelHeaderContainer>

      {isMountedThreads && (
        <>
          {threadTab === 'resolved' &&
            threadsWithoutEditor.map((group, groupIndex) => (
              <div key={`group-${group.position}-${groupIndex}`}>
                {group.threads.map(({ thread }, threadIndex) => (
                  <Thread
                    key={thread.id}
                    thread={thread}
                    position={group.position}
                    isActive={thread.id === activeThreadId}
                    onThreadClick={handleThreadClick}
                    setActiveThreadId={setActiveThreadId}
                    onAddComment={addComment}
                    onResolveThread={resolveThread}
                    onMarkActiveThread={markActiveThread}
                    onEditComment={editComment}
                    onDeleteComment={deleteComment}
                    onDeleteThread={deleteThread}
                    threadPanelRef={threadPanelRef}
                    threadIndex={threadIndex}
                    isWithoutEditor
                    openedGroups={openedGroups}
                  />
                ))}
              </div>
            ))}

          {filteredGroupedThreads.map((group, groupIndex) => (
            <div key={`group-${group.position}-${groupIndex}`}>
              {group.threads.map(({ thread }, threadIndex) => (
                <Thread
                  key={thread.id}
                  thread={thread}
                  position={group.position}
                  isActive={thread.id === activeThreadId}
                  onThreadClick={handleThreadClick}
                  setActiveThreadId={setActiveThreadId}
                  onAddComment={addComment}
                  onResolveThread={resolveThread}
                  onMarkActiveThread={markActiveThread}
                  onEditComment={editComment}
                  onDeleteComment={deleteComment}
                  onDeleteThread={deleteThread}
                  threadPanelRef={threadPanelRef}
                  totalThreadsInGroup={group.threads.length}
                  threadIndex={threadIndex}
                  isOpenedGroup={openedGroups === groupIndex}
                  openedGroups={openedGroups}
                />
              ))}
            </div>
          ))}
        </>
      )}
      <ThreadInput threadPanelRef={threadPanelRef} contentId={contentId} />
    </PyThreadPanelContainer>
  );
};
